import { useState, useCallback, useEffect, useMemo } from 'react';
import { useApi } from '../../../containers/ApiContainer';
import { useActiveFBOs } from '../../../containers/ActiveFBOContainer';
import {
  InboxItem,
  GroupedInboxItems,
  InboxFilters,
  matchesTimeFilter,
} from '../../OperationsInbox/types';

/**
 * Hook for fetching and managing operations queue data
 * Simplified version of useOperationsInboxData for the Ops view
 */
export function useOpsQueue() {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();

  const [items, setItems] = useState<InboxItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Filter state
  const [filters, setFilters] = useState<InboxFilters>({
    search: '',
    showSnoozed: false,
    timeFilter: 'week', // Default to week view
  });

  // Fetch queue items
  const fetchItems = useCallback(async () => {
    if (!activeFBO?.id || !postgrest) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await postgrest
        .from('operations_inbox_v')
        .select('*')
        .eq('fbo_id', activeFBO.id)
        .order('item_type', { ascending: true })
        .order('days_until_arrival', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: false });

      if (fetchError) throw new Error(fetchError.message);

      setItems(data || []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch queue'));
    } finally {
      setIsLoading(false);
    }
  }, [activeFBO?.id, postgrest]);

  // Initial fetch
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchItems, 60000);
    return () => clearInterval(interval);
  }, [fetchItems]);

  // Filter items by search and time
  const filteredItems = useMemo(() => {
    let result = items;

    // Apply search filter
    if (filters.search) {
      const query = filters.search.toLowerCase();
      result = result.filter(
        (item) =>
          item.tail_number?.toLowerCase().includes(query) ||
          item.operator_name?.toLowerCase().includes(query) ||
          item.aircraft_type_hint?.toLowerCase().includes(query) ||
          item.aircraft_make?.toLowerCase().includes(query) ||
          item.aircraft_model?.toLowerCase().includes(query)
      );
    }

    // Apply time filter
    result = result.filter((item) => matchesTimeFilter(item, filters.timeFilter));

    return result;
  }, [items, filters]);

  // Group items by type
  const groups: GroupedInboxItems = useMemo(() => {
    const unknownAircraft: InboxItem[] = [];
    const needsRouting: InboxItem[] = [];

    filteredItems.forEach((item) => {
      if (item.item_type === 'quarantine') {
        unknownAircraft.push(item);
      } else {
        needsRouting.push(item);
      }
    });

    return {
      unknownAircraft,
      needsRouting,
      snoozed: [], // Not showing snoozed in Ops view
    };
  }, [filteredItems]);

  return {
    items: filteredItems,
    groups,
    totalCount: items.length,
    isLoading,
    error,
    refetch: fetchItems,
    filters,
    setFilters,
  };
}
